import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, Zap, Shield, TrendingUp, ArrowRight, Building2 } from 'lucide-react'
import SEO from '../components/SEO'
import ProductCarousel from '../components/ProductCarousel'
import ProductCarouselSkeleton from '../components/ProductCarouselSkeleton'
import apiService from '../services/api'
import { useAuthStore } from '../store/authStore'
import { isEnterpriseUser } from '../utils/userPermissions'
import { useLocalization } from '../hooks/useLocalization'
import PartnershipHeroSection from '../components/partnership/PartnershipHeroSection'
import PartnershipBenefitsSection from '../components/partnership/PartnershipBenefitsSection'
import ModuleOverviewSection from '../components/partnership/ModuleOverviewSection'
import PartnerCollaborationSection from '../components/partnership/PartnerCollaborationSection'
import PartnershipCTASection from '../components/partnership/PartnershipCTASection'

// Define types locally to avoid import issues
interface Product {
  id: string
  title: string
  price: number
  imageUrls?: string[]
  isAuction?: boolean
  auctionEndDate?: string | Date
  owner?: {
    firstName?: string
    lastName?: string
  }
}

interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
}

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  // Check if user is enterprise user to hide marketplace links
  const { user } = useAuthStore()
  const isEnterprise = isEnterpriseUser(user)
  const { t } = useLocalization()

  const featuredCategories = [
    {
      name: 'Solar Panels',
      description: 'High-efficiency photovoltaic panels',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    },
    {
      name: 'Inverters',
      description: 'Power conversion systems',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    },
    {
      name: 'Batteries',
      description: 'Energy storage solutions',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    },
    {
      name: 'Mounting Systems',
      description: 'Installation hardware',
      image: 'https://images.unsplash.com/photo-1622541948011-8c8b76bb8b63?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    }
  ]

  // Mock data as fallback
  const mockFeaturedProducts = [
    {
      id: '1',
      title: 'High-Efficiency 400W Solar Panel',
      price: 299.99,
      originalPrice: 349.99,
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.8,
      reviews: 124,
      seller: 'SolarTech Pro',
      isAuction: false
    },
    {
      id: '2',
      title: '5kW String Inverter - Premium Grade',
      price: 1299.99,
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.9,
      reviews: 89,
      seller: 'PowerSolutions',
      isAuction: true,
      timeLeft: '2d 14h'
    },
    {
      id: '3',
      title: 'Tesla Powerwall 2 - Like New',
      price: 8999.99,
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 5.0,
      reviews: 45,
      seller: 'EnergyHub',
      isAuction: false
    },
    {
      id: '4',
      title: 'Complete Solar Kit 10kW System',
      price: 12999.99,
      originalPrice: 15999.99,
      image: 'https://images.unsplash.com/photo-1622541948011-8c8b76bb8b63?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.7,
      reviews: 67,
      seller: 'SolarComplete',
      isAuction: false
    },
    {
      id: '5',
      title: 'Microinverter Set - 20 Units',
      price: 2499.99,
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.6,
      reviews: 156,
      seller: 'MicroPower',
      isAuction: false
    },
    {
      id: '6',
      title: 'Solar Charge Controller MPPT 60A',
      price: 189.99,
      originalPrice: 229.99,
      image: 'https://images.unsplash.com/photo-1593941707882-a5bac6861d75?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.5,
      reviews: 203,
      seller: 'ChargeMax',
      isAuction: true,
      timeLeft: '1d 8h'
    },
    {
      id: '7',
      title: 'Flexible Solar Panel 100W',
      price: 149.99,
      image: 'https://images.unsplash.com/photo-1564419434-d6b63c27fac5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.4,
      reviews: 89,
      seller: 'FlexSolar',
      isAuction: false
    },
    {
      id: '8',
      title: 'Solar Monitoring System Pro',
      price: 399.99,
      image: 'https://images.unsplash.com/photo-1471219743851-c4df8b6ee585?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      rating: 4.8,
      reviews: 76,
      seller: 'MonitorTech',
      isAuction: false
    }
  ]

  useEffect(() => {
    fetchFeaturedProducts()
  }, [])

  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true)
      // Try to fetch real products from API
      const response = await apiService.get<ApiResponse<Product[]>>('/api/products/search?limit=8&status=ACTIVE')
      
      if (response.success && response.data) {
        // Handle nested response structure
        const products = Array.isArray(response.data) ? response.data : (response.data as any).products || []
        
        if (products.length > 0) {
          // Transform API products to match carousel interface
          const transformedProducts = products.map((product: Product) => ({
            id: product.id,
            title: product.title,
            price: parseFloat(product.price.toString()),
            image: product.imageUrls?.[0] || 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
            rating: 4.5, // Default rating since we don't have reviews yet
            reviews: Math.floor(Math.random() * 200) + 10, // Random reviews for demo
            seller: product.owner?.firstName ? `${product.owner.firstName} ${product.owner.lastName}` : 'Evoltbay Seller',
            isAuction: product.isAuction || false,
            timeLeft: product.isAuction && product.auctionEndDate ? 
              calculateTimeLeft(product.auctionEndDate) : undefined
          }))
          
          setFeaturedProducts(transformedProducts)
        } else {
          // If no real products, use mock data but mark them as mock
          const mockProductsWithFlag = mockFeaturedProducts.map(product => ({
            ...product,
            isMock: true
          }))
          setFeaturedProducts(mockProductsWithFlag)
        }
      } else {
        // Fallback to mock data if API fails
        const mockProductsWithFlag = mockFeaturedProducts.map(product => ({
          ...product,
          isMock: true
        }))
        setFeaturedProducts(mockProductsWithFlag)
      }
    } catch (error) {
      console.error('Failed to fetch featured products:', error)
      // Fallback to mock data
      const mockProductsWithFlag = mockFeaturedProducts.map(product => ({
        ...product,
        isMock: true
      }))
      setFeaturedProducts(mockProductsWithFlag)
    } finally {
      setLoading(false)
    }
  }

  const calculateTimeLeft = (endDate: string | Date) => {
    const end = new Date(endDate)
    const now = new Date()
    const diff = end.getTime() - now.getTime()
    
    if (diff <= 0) return 'Ended'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) {
      return `${days}d ${hours}h`
    } else {
      return `${hours}h`
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="EvoltBay - Solar Energy Ecommerce | Enterprise Quotation System | Government Solar Contracting"
        description="EvoltBay is the leading solar energy ecommerce platform and enterprise quotation system for solar equipment. Specializing in government solar contracting, we connect buyers with certified vendors for solar panels, inverters, batteries, and complete solar systems. Streamline your solar procurement with our advanced quotation system."
        keywords="solar energy ecommerce, enterprise quotation system, government solar contracting, quotation system, solar marketplace, solar panels, solar equipment, renewable energy, solar inverters, solar batteries, solar procurement, solar contracting, solar vendors, solar RFQ, solar quotes, solar energy marketplace"
        url={window.location.href}
        type="website"
      />
      
      <div className="flex-1">
        {/* Partnership Hero Section */}
        <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                  {t('home.hero.title')}
                </h1>
                <p className="text-xl mb-8 text-primary-100 dark:text-gray-300">
                  {t('home.hero.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  {!isEnterprise && (
                    <>
                      <Link
                        to="/products"
                        className="btn bg-white text-primary-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 px-8 py-3 text-lg font-semibold"
                      >
                        <Search className="w-5 h-5 mr-2" />
                        {t('home.hero.cta.browseProducts')}
                      </Link>
                      <Link
                        to="/auctions"
                        className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-gray-900 px-8 py-3 text-lg font-semibold"
                      >
                        {t('home.hero.cta.viewAuctions')}
                      </Link>
                    </>
                  )}
                  {isEnterprise && (
                    <Link
                      to="/enterprise"
                      className="btn bg-white text-primary-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 px-8 py-3 text-lg font-semibold"
                    >
                      <Building2 className="w-5 h-5 mr-2" />
                      {t('home.hero.cta.exploreEnterprise')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('home.features.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-gray-600 dark:text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {t('home.features.secureTransactions.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('home.features.secureTransactions.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-gray-600 dark:text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {t('home.features.qualityVerified.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('home.features.qualityVerified.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-gray-600 dark:text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {t('home.features.bestPrices.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('home.features.bestPrices.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Hero Section */}
      <PartnershipHeroSection />

      {/* Partnership Benefits Section */}
      <PartnershipBenefitsSection />

      {/* Module Overview Section */}
      <ModuleOverviewSection />

      {/* Partner Collaboration Section */}
      <PartnerCollaborationSection />

      {/* Partnership CTA Section */}
      <PartnershipCTASection />

      {/* About EvoltBay Section - SEO Optimized */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {t('home.about.title')}
            </h2>
            <div className="prose prose-lg dark:prose-invert max-w-none text-left">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                {t('home.about.description')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-white dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                    {t('home.about.ecommerce.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('home.about.ecommerce.description')}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                    {t('home.about.quotation.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('home.about.quotation.description')}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-700 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                    {t('home.about.contracting.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {t('home.about.contracting.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section - Hidden for Enterprise Users */}
      {!isEnterprise && (
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('home.categories.title')}
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                {t('home.categories.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCategories.map((category, index) => (
                <Link
                  key={index}
                  to={`/search?category=${encodeURIComponent(category.name)}`}
                  className="group bg-white dark:bg-gray-700 hover:shadow-lg dark:hover:shadow-xl transition-all duration-300 overflow-hidden rounded-lg"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{category.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products - Hidden for Enterprise Users */}
      {!isEnterprise && (
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-16">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  {t('home.featured.title')}
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300">
                  {t('home.featured.subtitle')}
                </p>
              </div>
              <Link
                to="/search"
                className="btn-outline dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 flex items-center"
              >
                {t('home.featured.viewAll')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {loading ? (
              <ProductCarouselSkeleton />
            ) : (
              <ProductCarousel
                products={featuredProducts}
                autoPlay={true}
                autoPlayInterval={50000}
                showDots={false}
                itemsPerView={{
                  mobile: 1,
                  tablet: 2,
                  desktop: 4
                }}
              />
            )}
          </div>
        </section>
      )}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {isEnterprise ? t('home.cta.enterprise.title') : t('home.cta.general.title')}
            </h2>
            <p className="text-xl mb-8 text-gray-300 max-w-3xl mx-auto">
              {isEnterprise ? t('home.cta.enterprise.subtitle') : t('home.cta.general.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isEnterprise ? (
                <>
                  <Link
                    to="/enterprise"
                    className="btn bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
                  >
                    <Building2 className="w-5 h-5 mr-2" />
                    {t('home.cta.enterprise.exploreEnterprise')}
                  </Link>
                  <Link
                    to="/enterprise/rfqs"
                    className="btn border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 text-lg font-semibold"
                  >
                    {t('home.cta.enterprise.viewRfqs')}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="btn bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
                  >
                    {t('home.cta.general.getStarted')}
                  </Link>
                  <Link
                    to="/search"
                    className="btn border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 text-lg font-semibold"
                  >
                    {t('home.cta.general.exploreProducts')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
} 